import { ColumnDef } from '@tanstack/react-table';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import { FormSelect } from '../../../../../components/tables/filterForms/FormSelect';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useDataContext } from '../../../../../context';
import { useToast } from '../../../../../context';
import { useListFiltersInUrl } from '../../../../../hooks';
import { useGetAllOrganizationsQuery } from '../../../../../services/endpoints/places/organization';
import { getCurrentUser } from '../../../../../services/helper';
import { SFCLocation, SFCOrganization } from '../../../../../types/place.types';
import { getFilterValue } from '../../../../../utils/listFilterSearchParams';
import {Forbidden} from "../../../../../components/error/Forbidden";
import { canViewOrganizations } from '../../../../../utils/rbac';

interface DataRow extends SFCOrganization {}

export const OrganizationsView = () => {
  const { addToast } = useToast();
  const { locationOptions } = useDataContext();
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const userHasAccess = canViewOrganizations(currentUser);

  const [filters, setFilters] = useListFiltersInUrl(
    { scope: 'all', data: { page: 1, entries: 20 } },
    { omitScopeFromUrl: true }
  );
  const { data, error, isLoading, isFetching } = useGetAllOrganizationsQuery(filters, {
    skip: !userHasAccess
  });

  const filterFormFields: FilterField[] = [
    {
      name: 'name',
      label: 'Name',
      value: getFilterValue(filters.data?.filterBy, 'name')
    },
    ...(currentUser?.isGlobal
      ? [{
          name: 'location_id',
          label: 'Location',
          type: 'number' as const,
          element: (
              <FormSelect
                  name="location_id"
                  label="Location"
                  options={locationOptions}
                  selected_Value={locationOptions.find(
                      (option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'location_id')
                  )}
              />
          )
        }]
      : [])
  ];

  const columns: ColumnDef<DataRow>[] = [
    {
      header: 'Name',
      accessorKey: 'name'
    },
    {
      header: 'Location',
      accessorKey: 'location',
      enableSorting: false,
      cell: ({ getValue }) => {
        let location = getValue() as SFCLocation;
        return (
            <LinkCell
                content={location?.title ?? 'error'}
                url={`/admin/places/locations/${location?.id ?? 0}`}
            />
        );
      }
    },
    {
      header: '',
      accessorKey: 'blank',
      enableSorting: false,
      cell: ({ row }) => (
          <>
            <LinkCell
                content="View"
                url={`/admin/places/organizations/${row.original.id}`}
            />
            &nbsp; &nbsp;
            <LinkCell
                content="Edit"
                url={`/admin/places/organizations/${row.original.id}/edit`}
            />
          </>
      )
    }
  ];

  useEffect(() => {
    if (error) {
      addToast(`Error while loading data: ${JSON.stringify(error)}`, 'error');
    }
  }, [error]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  if (!userHasAccess) {
    return <Forbidden />;
  }

  if (error || isLoading) {
    return <></>;
  }

  return (
      <div className="py-2">
        <SFCTable
            name={'Organizations'}
            defaultScope={filters.scope ?? 'all'}
            columns={columns}
            data={data?.results ?? []}
            count={data?.count}
            isLoading={isLoading}
            isFetching={isFetching}
            indexDownloadPath="organizations"
            filters={filters?.data?.filterBy}
            page_Size={filters?.data?.entries}
            filterForm={
              <FilterForm
                  fields={filterFormFields}
                  onApplyFilters={setFilterBy}
              />
            }
            onFiltersChanged={setFilters}
            newItemEl={
              <SimpleButton
                  variant="sfc-blueberry"
                  height={40}
                  truncate={false}
                  onClick={() => navigate(`/admin/places/organizations/new`)}
              >
                New Organization
              </SimpleButton>
            }
        />
      </div>
  );
};